import { useCallback, useEffect, useRef, useState } from "react"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import {
  registerChoiceConfirmOpener,
  registerConfirmOpener,
  registerPasswordConfirmOpener,
  type ConfirmChoice,
  type ConfirmRequest,
} from "@/lib/confirm"

/** Matches `.ui-dialog-*-out` duration in application.css */
const CONFIRM_EXIT_MS = 120

type PendingConfirm = ConfirmRequest & {
  mode: "confirm" | "password" | "choice"
  resolveConfirm: (value: boolean) => void
  resolvePassword: (value: string | null) => void
  resolveChoice: (value: ConfirmChoice | null) => void
}

function cancelPending(pending: PendingConfirm) {
  if (pending.mode === "password") pending.resolvePassword(null)
  else if (pending.mode === "choice") pending.resolveChoice(null)
  else pending.resolveConfirm(false)
}

export function ConfirmHost() {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const [open, setOpen] = useState(false)
  const pendingRef = useRef<PendingConfirm | null>(null)
  const clearTimerRef = useRef<number | null>(null)

  const clearClearTimer = useCallback(() => {
    if (clearTimerRef.current !== null) {
      window.clearTimeout(clearTimerRef.current)
      clearTimerRef.current = null
    }
  }, [])

  const finishPending = useCallback(() => {
    pendingRef.current = null
    setOpen(false)
    clearClearTimer()
    clearTimerRef.current = window.setTimeout(() => {
      clearTimerRef.current = null
      setPending(null)
    }, CONFIRM_EXIT_MS)
  }, [clearClearTimer])

  const settleCancel = useCallback(() => {
    const current = pendingRef.current
    if (!current) return
    cancelPending(current)
    finishPending()
  }, [finishPending])

  const settleConfirm = useCallback(
    (password?: string) => {
      const current = pendingRef.current
      if (!current) return
      if (current.mode === "password") current.resolvePassword(password ?? null)
      else if (current.mode === "choice") current.resolveChoice("confirm")
      else current.resolveConfirm(true)
      finishPending()
    },
    [finishPending],
  )

  const settleAlternative = useCallback(() => {
    const current = pendingRef.current
    if (!current) return
    if (current.mode === "choice") current.resolveChoice("alternative")
    else cancelPending(current)
    finishPending()
  }, [finishPending])

  useEffect(() => {
    const show = (next: PendingConfirm) => {
      const previous = pendingRef.current
      if (previous) cancelPending(previous)
      clearClearTimer()
      pendingRef.current = next
      setPending(next)
      setOpen(true)
    }
    const noop = () => undefined

    registerConfirmOpener((request) => {
      return new Promise<boolean>((resolve) => {
        show({ ...request, mode: "confirm", resolveConfirm: resolve, resolvePassword: noop, resolveChoice: noop })
      })
    })

    registerPasswordConfirmOpener((request) => {
      return new Promise<string | null>((resolve) => {
        show({
          ...request,
          requirePassword: true,
          mode: "password",
          resolveConfirm: noop,
          resolvePassword: resolve,
          resolveChoice: noop,
        })
      })
    })

    registerChoiceConfirmOpener((request) => {
      return new Promise<ConfirmChoice | null>((resolve) => {
        show({ ...request, mode: "choice", resolveConfirm: noop, resolvePassword: noop, resolveChoice: resolve })
      })
    })

    return () => {
      registerConfirmOpener(null)
      registerPasswordConfirmOpener(null)
      registerChoiceConfirmOpener(null)
      clearClearTimer()
      const current = pendingRef.current
      if (current) {
        cancelPending(current)
        pendingRef.current = null
      }
    }
  }, [clearClearTimer])

  return (
    <ConfirmActionDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) settleCancel()
      }}
      title={pending?.title}
      message={pending?.message ?? ""}
      confirmLabel={pending?.confirmLabel}
      cancelLabel={pending?.cancelLabel}
      tone={pending?.tone}
      requirePassword={pending?.requirePassword}
      passwordLabel={pending?.passwordLabel}
      alternativeLabel={pending?.alternativeLabel}
      alternativeTone={pending?.alternativeTone}
      onAlternative={settleAlternative}
      onConfirm={(password) => settleConfirm(password)}
    />
  )
}
